package com.arenaproplay.admin.pages.system

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.domain.admin.AuditEntry
import com.arenaproplay.admin.ui.components.admin.*
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.mono
import com.arenaproplay.admin.ui.utils.formatTimestampShort
import org.jetbrains.compose.web.css.*

// ── Constants ─────────────────────────────────────────────────────────────────

const val SYSTEM_AUDIT_PAGE_SIZE = 50

// ── Private helpers ───────────────────────────────────────────────────────────

private fun sysAuditActionPillTone(action: String): PillTone {
    val a = action.uppercase()
    return when {
        a.contains("DELETE") || a.contains("REMOVE") || a.contains("REVOKE") -> PillTone.Danger
        a.contains("CREATE") || a.contains("GRANT") || a.contains("APPROVE") -> PillTone.Success
        a.contains("UPDATE") || a.contains("CHANGE") || a.contains("EDIT") -> PillTone.Accent
        else -> PillTone.Neutral
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 2: АУДИТ
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun SystemAuditTab(
    loadState: LoadState,
    data: List<AuditEntry>?,
    filtered: List<AuditEntry>,
    offset: Long,
    onPrev: () -> Unit,
    onNext: () -> Unit,
) {
    if (loadState.error != null) {
        ErrorCard(loadState)
        return
    }

    SectionCard(
        title = "Журнал действий",
        subtitle = "Каждая операция администратора с меткой времени и IP",
    ) {
        DataTable(
            items = filtered,
            loading = data == null,
            columns = listOf(
                TableColumn<AuditEntry>(
                    header = "ВРЕМЯ",
                    weight = 1.2f,
                    sortKey = { it.createdAt },
                ) {
                    SpanText(
                        formatTimestampShort(it.createdAt),
                        Modifier.mono(12).color(AppColors.Muted),
                    )
                },
                TableColumn<AuditEntry>(
                    header = "ДЕЙСТВИЕ",
                    weight = 1.4f,
                ) {
                    Pill(it.action, sysAuditActionPillTone(it.action))
                },
                TableColumn<AuditEntry>(
                    header = "АДМИНИСТРАТОР",
                    weight = 1.0f,
                ) {
                    SpanText("#${it.adminId.take(8)}", Modifier.mono(12).color(AppColors.Ink))
                },
                TableColumn<AuditEntry>(
                    header = "ОБЪЕКТ",
                    weight = 1.6f,
                ) {
                    Column {
                        SpanText(it.targetKind ?: "—", Modifier.bodySMedium().color(AppColors.Ink))
                        if (it.targetId != null) {
                            Box(Modifier.height(2.px))
                            SpanText("#${it.targetId.take(12)}", Modifier.mono(11).color(AppColors.Muted))
                        }
                    }
                },
                TableColumn<AuditEntry>(
                    header = "IP",
                    weight = 0.8f,
                ) {
                    SpanText(it.ip ?: "—", Modifier.mono(12).color(AppColors.Faint))
                },
            ),
            emptyText = "Нет записей",
        )
    }

    VSpace(16)

    // Offset pager — next enabled when count == page size
    val count = filtered.size
    val from = if (count == 0) 0L else offset + 1
    val to = offset + count
    Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
        SpanText("Записи $from–$to", Modifier.bodySMedium().color(AppColors.Muted))
        Box(Modifier.weight(1f))
        PagerButton("‹ Старше", onPrev, offset > 0)
        HSpace(8)
        PagerButton("Новее ›", onNext, count == SYSTEM_AUDIT_PAGE_SIZE)
    }

    VSpace(28)
}
